import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Button,
  Space,
  Alert,
  Typography,
  Empty,
  DatePicker,
  Radio,
  Tag,
} from 'antd';
import {
  SaveOutlined,
  SendOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  TeamOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  SettingOutlined,
  SafetyOutlined,
} from '@ant-design/icons';

// 精致的翡翠绿色彩系统
const EMERALD_COLORS = {
  primary: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981', // 主色
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
  },
  semantic: {
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
    pending: '#94a3b8',
    completed: '#10b981',
    inProgress: '#f59e0b',
  },
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface TaskContentProps {
  step: string;
  task: string | null;
  projectCode: string;
  userRole?: string;
  hideActionButtons?: boolean; // 新增：是否隐藏操作按钮
}

const TaskContent: React.FC<TaskContentProps> = ({
  step,
  task,
  projectCode,
  userRole,
  hideActionButtons = false,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // 如果没有选择任务，显示提示
  if (!task) {
    return (
      <Card>
        <Empty
          description={
            <div>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                {t('project:taskContent.empty.selectTaskTitle')}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {t('project:taskContent.empty.selectTaskDesc')}
              </div>
            </div>
          }
        />
      </Card>
    );
  }

  // 渲染不同任务的内容
  const renderTaskContent = () => {
    switch (task) {
      case 'financial_analysis':
        return (
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      padding: '10px',
                      borderRadius: '10px',
                      background: `linear-gradient(135deg, ${EMERALD_COLORS.primary[100]} 0%, ${EMERALD_COLORS.primary[200]} 100%)`,
                      marginRight: '12px',
                    }}
                  >
                    <DollarOutlined
                      style={{
                        color: EMERALD_COLORS.primary[600],
                        fontSize: '20px',
                      }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: EMERALD_COLORS.neutral[800],
                        marginBottom: '2px',
                      }}
                    >
                      {t('project:taskContent.financialAnalysis.title')}
                    </div>
                    <Text
                      style={{
                        fontSize: '14px',
                        color: EMERALD_COLORS.neutral[500],
                      }}
                    >
                      {t('project:taskContent.financialAnalysis.description')}
                    </Text>
                  </div>
                </div>
                <Tag
                  style={{
                    background: `linear-gradient(135deg, ${EMERALD_COLORS.semantic.inProgress}15 0%, ${EMERALD_COLORS.semantic.inProgress}25 100%)`,
                    border: `1px solid ${EMERALD_COLORS.semantic.inProgress}30`,
                    color: EMERALD_COLORS.semantic.inProgress,
                    borderRadius: '8px',
                    padding: '4px 12px',
                    fontWeight: '500',
                  }}
                >
                  {t('project:taskContent.financialAnalysis.estimated')}
                </Tag>
              </div>
            }
            style={{
              marginBottom: '24px',
              borderRadius: '16px',
              border: `1px solid ${EMERALD_COLORS.neutral[200]}`,
              boxShadow: '0 4px 20px -4px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
            }}
            headStyle={{
              background: `linear-gradient(135deg, ${EMERALD_COLORS.neutral[50]} 0%, ${EMERALD_COLORS.primary[50]} 100%)`,
              borderBottom: `1px solid ${EMERALD_COLORS.neutral[200]}`,
              padding: '20px 24px',
            }}
            bodyStyle={{
              padding: '24px',
            }}
          >
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.totalAssets.label'
                    )}
                    name={['financialAnalysis', 'totalAssets']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.totalAssets.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.totalAssets.placeholder'
                      )}
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.totalLiabilities.label'
                    )}
                    name={['financialAnalysis', 'totalLiabilities']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.totalLiabilities.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.totalLiabilities.placeholder'
                      )}
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.netAssets.label'
                    )}
                    name={['financialAnalysis', 'netAssets']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.netAssets.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.netAssets.placeholder'
                      )}
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.revenue.label'
                    )}
                    name={['financialAnalysis', 'revenue']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.revenue.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.revenue.placeholder'
                      )}
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.netProfit.label'
                    )}
                    name={['financialAnalysis', 'netProfit']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.netProfit.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.netProfit.placeholder'
                      )}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.operatingCashFlow.label'
                    )}
                    name={['financialAnalysis', 'operatingCashFlow']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.operatingCashFlow.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.operatingCashFlow.placeholder'
                      )}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.debtRatio.label'
                    )}
                    name={['financialAnalysis', 'debtRatio']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.debtRatio.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.debtRatio.placeholder'
                      )}
                      min={0}
                      max={100}
                      formatter={value => `${value}%`}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.currentRatio.label'
                    )}
                    name={['financialAnalysis', 'currentRatio']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.currentRatio.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.currentRatio.placeholder'
                      )}
                      min={0}
                      step={0.01}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.roe.label'
                    )}
                    name={['financialAnalysis', 'roe']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.roe.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.roe.placeholder'
                      )}
                      min={0}
                      formatter={value => `${value}%`}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.financialAnalysis.fields.creditRating.label'
                    )}
                    name={['financialAnalysis', 'creditRating']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.financialAnalysis.fields.creditRating.required'
                        ),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t(
                        'project:taskContent.financialAnalysis.fields.creditRating.placeholder'
                      )}
                    >
                      <Option value="AAA">AAA</Option>
                      <Option value="AA+">AA+</Option>
                      <Option value="AA">AA</Option>
                      <Option value="AA-">AA-</Option>
                      <Option value="A+">A+</Option>
                      <Option value="A">A</Option>
                      <Option value="A-">A-</Option>
                      <Option value="BBB+">BBB+</Option>
                      <Option value="BBB">BBB</Option>
                      <Option value="other">
                        {t(
                          'project:taskContent.financialAnalysis.fields.creditRating.options.other'
                        )}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {userRole === 'issuer' && (
              <Alert
                message={t('project:taskContent.financialAnalysis.tips.title')}
                description={
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>
                      {t('project:taskContent.financialAnalysis.tips.item1')}
                    </li>
                    <li>
                      {t('project:taskContent.financialAnalysis.tips.item2')}
                    </li>
                    <li>
                      {t('project:taskContent.financialAnalysis.tips.item3')}
                    </li>
                  </ul>
                }
                type="success"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </Card>
        );

      case 'green_project':
        return (
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      padding: '10px',
                      borderRadius: '10px',
                      background: `linear-gradient(135deg, ${EMERALD_COLORS.primary[100]} 0%, ${EMERALD_COLORS.primary[200]} 100%)`,
                      marginRight: '12px',
                    }}
                  >
                    <EnvironmentOutlined
                      style={{
                        color: EMERALD_COLORS.primary[600],
                        fontSize: '20px',
                      }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: EMERALD_COLORS.neutral[800],
                        marginBottom: '2px',
                      }}
                    >
                      {t('project:taskContent.greenProject.title')}
                    </div>
                    <Text
                      style={{
                        fontSize: '14px',
                        color: EMERALD_COLORS.neutral[500],
                      }}
                    >
                      {t('project:taskContent.greenProject.description')}
                    </Text>
                  </div>
                </div>
                <Tag
                  style={{
                    background: `linear-gradient(135deg, ${EMERALD_COLORS.semantic.inProgress}15 0%, ${EMERALD_COLORS.semantic.inProgress}25 100%)`,
                    border: `1px solid ${EMERALD_COLORS.semantic.inProgress}30`,
                    color: EMERALD_COLORS.semantic.inProgress,
                    borderRadius: '8px',
                    padding: '4px 12px',
                    fontWeight: '500',
                  }}
                >
                  {t('project:taskContent.greenProject.estimated')}
                </Tag>
              </div>
            }
            style={{
              marginBottom: '24px',
              borderRadius: '16px',
              border: `1px solid ${EMERALD_COLORS.neutral[200]}`,
              boxShadow: '0 4px 20px -4px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
            }}
            headStyle={{
              background: `linear-gradient(135deg, ${EMERALD_COLORS.neutral[50]} 0%, ${EMERALD_COLORS.primary[50]} 100%)`,
              borderBottom: `1px solid ${EMERALD_COLORS.neutral[200]}`,
              padding: '20px 24px',
            }}
            bodyStyle={{
              padding: '24px',
            }}
          >
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.greenProject.fields.projectName.label'
                    )}
                    name={['greenProjectAssessment', 'projectName']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.greenProject.fields.projectName.required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'project:taskContent.greenProject.fields.projectName.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.greenProject.fields.projectLocation.label'
                    )}
                    name={['greenProjectAssessment', 'projectLocation']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.greenProject.fields.projectLocation.required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'project:taskContent.greenProject.fields.projectLocation.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.greenProject.fields.projectNature.label'
                    )}
                    name={['greenProjectAssessment', 'projectNature']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.greenProject.fields.projectNature.required'
                        ),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t(
                        'project:taskContent.greenProject.fields.projectNature.placeholder'
                      )}
                    >
                      <Option value="new">
                        {t(
                          'project:taskContent.greenProject.fields.projectNature.options.new'
                        )}
                      </Option>
                      <Option value="expansion">
                        {t(
                          'project:taskContent.greenProject.fields.projectNature.options.expansion'
                        )}
                      </Option>
                      <Option value="renovation">
                        {t(
                          'project:taskContent.greenProject.fields.projectNature.options.renovation'
                        )}
                      </Option>
                      <Option value="acquisition">
                        {t(
                          'project:taskContent.greenProject.fields.projectNature.options.acquisition'
                        )}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.greenProject.fields.totalInvestment.label'
                    )}
                    name={['greenProjectAssessment', 'totalInvestment']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.greenProject.fields.totalInvestment.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.greenProject.fields.totalInvestment.placeholder'
                      )}
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.greenProject.fields.constructionPeriod.label'
                    )}
                    name={['greenProjectAssessment', 'constructionPeriod']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.greenProject.fields.constructionPeriod.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.greenProject.fields.constructionPeriod.placeholder'
                      )}
                      min={0}
                      max={10}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.greenProject.fields.operationPeriod.label'
                    )}
                    name={['greenProjectAssessment', 'operationPeriod']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.greenProject.fields.operationPeriod.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.greenProject.fields.operationPeriod.placeholder'
                      )}
                      min={0}
                      max={50}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.greenProject.fields.needThirdPartyVerification.label'
                    )}
                    name={[
                      'greenProjectAssessment',
                      'needThirdPartyVerification',
                    ]}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.greenProject.fields.needThirdPartyVerification.required'
                        ),
                      },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value={true}>{t('common:label.yes')}</Radio>
                      <Radio value={false}>{t('common:label.no')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.greenProject.fields.annualEnergySaving.label'
                    )}
                    name={['greenProjectAssessment', 'annualEnergySaving']}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.greenProject.fields.annualEnergySaving.placeholder'
                      )}
                      min={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.greenProject.fields.annualCO2Reduction.label'
                    )}
                    name={['greenProjectAssessment', 'annualCO2Reduction']}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.greenProject.fields.annualCO2Reduction.placeholder'
                      )}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {userRole === 'exchange' && (
              <Alert
                message={t(
                  'project:taskContent.greenProject.reviewPoints.title'
                )}
                description={
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>
                      {t('project:taskContent.greenProject.reviewPoints.item1')}
                    </li>
                    <li>
                      {t('project:taskContent.greenProject.reviewPoints.item2')}
                    </li>
                    <li>
                      {t('project:taskContent.greenProject.reviewPoints.item3')}
                    </li>
                    <li>
                      {t('project:taskContent.greenProject.reviewPoints.item4')}
                    </li>
                  </ul>
                }
                type="warning"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </Card>
        );

      case 'issuance_plan':
        return (
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <SettingOutlined
                  style={{ marginRight: '8px', color: '#7c3aed' }}
                />
                {t('project:taskContent.issuancePlan.title')}
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  {t('project:taskContent.issuancePlan.estimated')}
                </Tag>
              </div>
            }
            style={{ marginBottom: '24px' }}
          >
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuancePlan.fields.plannedIssuanceSize.label'
                    )}
                    name={['issuancePlan', 'plannedIssuanceSize']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuancePlan.fields.plannedIssuanceSize.required'
                        ),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t(
                        'project:taskContent.issuancePlan.fields.plannedIssuanceSize.placeholder'
                      )}
                    >
                      <Option value="usd_300m_500m">
                        {t(
                          'project:taskContent.issuancePlan.fields.plannedIssuanceSize.options.rangeOne'
                        )}
                      </Option>
                      <Option value="usd_500m_1b">
                        {t(
                          'project:taskContent.issuancePlan.fields.plannedIssuanceSize.options.rangeTwo'
                        )}
                      </Option>
                      <Option value="usd_over_1b">
                        {t(
                          'project:taskContent.issuancePlan.fields.plannedIssuanceSize.options.rangeThree'
                        )}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuancePlan.fields.plannedTerm.label'
                    )}
                    name={['issuancePlan', 'plannedTerm']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuancePlan.fields.plannedTerm.required'
                        ),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t(
                        'project:taskContent.issuancePlan.fields.plannedTerm.placeholder'
                      )}
                    >
                      <Option value="3y">
                        {t(
                          'project:taskContent.issuancePlan.fields.plannedTerm.options.term3y'
                        )}
                      </Option>
                      <Option value="5y">
                        {t(
                          'project:taskContent.issuancePlan.fields.plannedTerm.options.term5y'
                        )}
                      </Option>
                      <Option value="7y">
                        {t(
                          'project:taskContent.issuancePlan.fields.plannedTerm.options.term7y'
                        )}
                      </Option>
                      <Option value="10y">
                        {t(
                          'project:taskContent.issuancePlan.fields.plannedTerm.options.term10y'
                        )}
                      </Option>
                      <Option value="other">
                        {t('project:taskContent.common.other')}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuancePlan.fields.guaranteeStructure.label'
                    )}
                    name={['issuancePlan', 'guaranteeStructure']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuancePlan.fields.guaranteeStructure.required'
                        ),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t(
                        'project:taskContent.issuancePlan.fields.guaranteeStructure.placeholder'
                      )}
                    >
                      <Option value="unsecured">
                        {t(
                          'project:taskContent.issuancePlan.fields.guaranteeStructure.options.unsecured'
                        )}
                      </Option>
                      <Option value="domestic_guarantee">
                        {t(
                          'project:taskContent.issuancePlan.fields.guaranteeStructure.options.domesticGuarantee'
                        )}
                      </Option>
                      <Option value="bank_sblc">
                        {t(
                          'project:taskContent.issuancePlan.fields.guaranteeStructure.options.bankSBLC'
                        )}
                      </Option>
                      <Option value="keepwell">
                        {t(
                          'project:taskContent.issuancePlan.fields.guaranteeStructure.options.keepwell'
                        )}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuancePlan.fields.targetExchange.label'
                    )}
                    name={['issuancePlan', 'targetExchange']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuancePlan.fields.targetExchange.required'
                        ),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t(
                        'project:taskContent.issuancePlan.fields.targetExchange.placeholder'
                      )}
                    >
                      <Option value="mox">
                        {t(
                          'project:taskContent.issuancePlan.fields.targetExchange.options.mox'
                        )}
                      </Option>
                      <Option value="hkex">
                        {t(
                          'project:taskContent.issuancePlan.fields.targetExchange.options.hkex'
                        )}
                      </Option>
                      <Option value="sgx">
                        {t(
                          'project:taskContent.issuancePlan.fields.targetExchange.options.sgx'
                        )}
                      </Option>
                      <Option value="other">
                        {t('project:taskContent.common.other')}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuancePlan.fields.expectedCouponRate.label'
                    )}
                    name={['issuancePlan', 'expectedCouponRate']}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.issuancePlan.fields.expectedCouponRate.placeholder'
                      )}
                      min={0}
                      max={20}
                      step={0.1}
                      formatter={value => `${value}%`}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'service_requirements':
        return (
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <SafetyOutlined
                  style={{ marginRight: '8px', color: '#dc2626' }}
                />
                {t('project:taskContent.serviceRequirements.title')}
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  {t('project:taskContent.serviceRequirements.estimated')}
                </Tag>
              </div>
            }
            style={{ marginBottom: '24px' }}
          >
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.serviceRequirements.fields.needFinancialAdvisor.label'
                    )}
                    name={['serviceRequirements', 'needFinancialAdvisor']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.serviceRequirements.fields.needFinancialAdvisor.required'
                        ),
                      },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value={true}>{t('common:label.yes')}</Radio>
                      <Radio value={false}>{t('common:label.no')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.serviceRequirements.fields.needUnderwriter.label'
                    )}
                    name={['serviceRequirements', 'needUnderwriter']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.serviceRequirements.fields.needUnderwriter.required'
                        ),
                      },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value={true}>{t('common:label.yes')}</Radio>
                      <Radio value={false}>{t('common:label.no')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.serviceRequirements.fields.needGreenCertification.label'
                    )}
                    name={['serviceRequirements', 'needGreenCertification']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.serviceRequirements.fields.needGreenCertification.required'
                        ),
                      },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value={true}>{t('common:label.yes')}</Radio>
                      <Radio value={false}>{t('common:label.no')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.serviceRequirements.fields.needLawFirm.label'
                    )}
                    name={['serviceRequirements', 'needLawFirm']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.serviceRequirements.fields.needLawFirm.required'
                        ),
                      },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value={true}>{t('common:label.yes')}</Radio>
                      <Radio value={false}>{t('common:label.no')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'risk_management':
        return (
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <SafetyOutlined
                  style={{ marginRight: '8px', color: '#f59e0b' }}
                />
                {t('project:taskContent.riskManagement.title')}
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  {t('project:taskContent.riskManagement.estimated')}
                </Tag>
              </div>
            }
            style={{ marginBottom: '24px' }}
          >
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={t(
                      'project:taskContent.riskManagement.fields.mainRiskFactors.label'
                    )}
                    name={['riskManagement', 'mainRiskFactors']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.riskManagement.fields.mainRiskFactors.required'
                        ),
                      },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder={t(
                        'project:taskContent.riskManagement.fields.mainRiskFactors.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={t(
                      'project:taskContent.riskManagement.fields.riskMitigationMeasures.label'
                    )}
                    name={['riskManagement', 'riskMitigationMeasures']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.riskManagement.fields.riskMitigationMeasures.required'
                        ),
                      },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder={t(
                        'project:taskContent.riskManagement.fields.riskMitigationMeasures.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.riskManagement.fields.hasInsurance.label'
                    )}
                    name={['riskManagement', 'hasInsurance']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.riskManagement.fields.hasInsurance.required'
                        ),
                      },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value={true}>{t('common:label.yes')}</Radio>
                      <Radio value={false}>{t('common:label.no')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.riskManagement.fields.riskRating.label'
                    )}
                    name={['riskManagement', 'riskRating']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.riskManagement.fields.riskRating.required'
                        ),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t(
                        'project:taskContent.riskManagement.fields.riskRating.placeholder'
                      )}
                    >
                      <Option value="low">
                        {t(
                          'project:taskContent.riskManagement.fields.riskRating.options.low'
                        )}
                      </Option>
                      <Option value="medium_low">
                        {t(
                          'project:taskContent.riskManagement.fields.riskRating.options.mediumLow'
                        )}
                      </Option>
                      <Option value="medium">
                        {t(
                          'project:taskContent.riskManagement.fields.riskRating.options.medium'
                        )}
                      </Option>
                      <Option value="medium_high">
                        {t(
                          'project:taskContent.riskManagement.fields.riskRating.options.mediumHigh'
                        )}
                      </Option>
                      <Option value="high">
                        {t(
                          'project:taskContent.riskManagement.fields.riskRating.options.high'
                        )}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'issuer_analysis':
        return (
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      padding: '10px',
                      borderRadius: '10px',
                      background: `linear-gradient(135deg, ${EMERALD_COLORS.primary[100]} 0%, ${EMERALD_COLORS.primary[200]} 100%)`,
                      marginRight: '12px',
                    }}
                  >
                    <TeamOutlined
                      style={{
                        color: EMERALD_COLORS.primary[600],
                        fontSize: '20px',
                      }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: EMERALD_COLORS.neutral[800],
                        marginBottom: '2px',
                      }}
                    >
                      {t('project:taskContent.issuerAnalysis.title')}
                    </div>
                    <Text
                      style={{
                        fontSize: '14px',
                        color: EMERALD_COLORS.neutral[500],
                      }}
                    >
                      {t('project:taskContent.issuerAnalysis.description')}
                    </Text>
                  </div>
                </div>
                <Tag
                  style={{
                    background: `linear-gradient(135deg, ${EMERALD_COLORS.semantic.inProgress}15 0%, ${EMERALD_COLORS.semantic.inProgress}25 100%)`,
                    border: `1px solid ${EMERALD_COLORS.semantic.inProgress}30`,
                    color: EMERALD_COLORS.semantic.inProgress,
                    borderRadius: '8px',
                    padding: '4px 12px',
                    fontWeight: '500',
                  }}
                >
                  {t('project:taskContent.issuerAnalysis.estimated')}
                </Tag>
              </div>
            }
            style={{
              marginBottom: '24px',
              borderRadius: '16px',
              border: `1px solid ${EMERALD_COLORS.neutral[200]}`,
              boxShadow: '0 4px 20px -4px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
            }}
            headStyle={{
              background: `linear-gradient(135deg, ${EMERALD_COLORS.neutral[50]} 0%, ${EMERALD_COLORS.primary[50]} 100%)`,
              borderBottom: `1px solid ${EMERALD_COLORS.neutral[200]}`,
              padding: '20px 24px',
            }}
            bodyStyle={{
              padding: '24px',
            }}
          >
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.companyNameCN.label'
                    )}
                    name={['issuerAnalysis', 'companyNameCN']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.companyNameCN.required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.companyNameCN.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.companyNameEN.label'
                    )}
                    name={['issuerAnalysis', 'companyNameEN']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.companyNameEN.required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.companyNameEN.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.registeredAddress.label'
                    )}
                    name={['issuerAnalysis', 'registeredAddress']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.registeredAddress.required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.registeredAddress.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.creditCode.label'
                    )}
                    name={['issuerAnalysis', 'creditCode']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.creditCode.required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.creditCode.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.registeredCapital.label'
                    )}
                    name={['issuerAnalysis', 'registeredCapital']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.registeredCapital.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.registeredCapital.placeholder'
                      )}
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.paidCapital.label'
                    )}
                    name={['issuerAnalysis', 'paidCapital']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.paidCapital.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.paidCapital.placeholder'
                      )}
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.establishDate.label'
                    )}
                    name={['issuerAnalysis', 'establishDate']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.establishDate.required'
                        ),
                      },
                    ]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.establishDate.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.controllingShareholderName.label'
                    )}
                    name={['issuerAnalysis', 'controllingShareholderName']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.controllingShareholderName.required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.controllingShareholderName.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.controllingShareholderRatio.label'
                    )}
                    name={['issuerAnalysis', 'controllingShareholderRatio']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.controllingShareholderRatio.required'
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.controllingShareholderRatio.placeholder'
                      )}
                      min={0}
                      max={100}
                      formatter={value => `${value}%`}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.actualController.label'
                    )}
                    name={['issuerAnalysis', 'actualController']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.actualController.required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'project:taskContent.issuerAnalysis.fields.actualController.placeholder'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t(
                      'project:taskContent.issuerAnalysis.fields.hasEquityPledge.label'
                    )}
                    name={['issuerAnalysis', 'hasEquityPledge']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'project:taskContent.issuerAnalysis.fields.hasEquityPledge.required'
                        ),
                      },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value={true}>{t('common:label.yes')}</Radio>
                      <Radio value={false}>{t('common:label.no')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'spo_process':
        // 动态导入SPO流程组件
        const SPOProcess = require('./professional-due-diligence').SPOProcess;
        return (
          <SPOProcess
            projectCode={projectCode}
            userRole={userRole}
            readonly={userRole === 'intermediary' || userRole === 'exchange'}
          />
        );

      default:
        return (
          <Card title={t('project:taskContent.fallback.taskTitle', { task })}>
            <Alert
              message={t('project:taskContent.fallback.empty')}
              description={t('project:taskContent.fallback.taskEmpty', {
                task,
              })}
              type="info"
              showIcon
            />
          </Card>
        );
    }
  };

  // 渲染操作按钮
  const renderActionButtons = () => (
    <Card>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Text type="secondary">
            {t('project:projectDetail.status.autoSaved')} •{' '}
            {t('project:projectDetail.status.lastSaved')}{' '}
            {new Date().toLocaleTimeString()}
          </Text>
        </div>

        <Space>
          {userRole === 'issuer' && (
            <>
              <Button icon={<SaveOutlined />}>
                {t('project:enhancedDetail.actions.saveDraft')}
              </Button>
              <Button type="primary" icon={<SendOutlined />}>
                {t('project:enhancedDetail.actions.submitTask')}
              </Button>
            </>
          )}

          {(userRole === 'exchange' || userRole === 'admin') && (
            <>
              <Button icon={<ArrowLeftOutlined />}>
                {t('project:projectDetail.actions.prevTask')}
              </Button>
              <Button icon={<ArrowRightOutlined />}>
                {t('project:projectDetail.actions.nextTask')}
              </Button>
              <Button type="primary">{t('common:action.approve')}</Button>
            </>
          )}
        </Space>
      </div>
    </Card>
  );

  return (
    <div>
      {renderTaskContent()}
      {!hideActionButtons && renderActionButtons()}
    </div>
  );
};

export default TaskContent;
